import { useId, useRef } from 'react';
import type { SiteEntity } from '../../../types/site';
import type { GenerationProjectInputChanges, GenerationProjectInputEditScope } from '../../../types/generationProjectInputSync';
import { generationProjectInputHash, assertGenerationProjectInputEditScope, declaredSharedProjectPue } from '../../../lib/generationImpact/generationProjectInputSync';
import { useGenerationProjectInputSync } from './useGenerationProjectInputSync';

interface Props {
  site: SiteEntity;
  editingAssessment: GenerationProjectInputEditScope;
  isMainProjectScope: boolean;
  readOnly: boolean;
  onSaved?: () => Promise<void>;
}

/** One canonical project-input editor in either assessment. It never derives
 * entered demand from acres, equipment output, recommendation or a result. */
export function GenerationProjectInputsPanel(props: Props) {
  let scopeAllowed = props.isMainProjectScope;
  try { assertGenerationProjectInputEditScope(props.site, props.editingAssessment); }
  catch { scopeAllowed = false; }
  return <GenerationProjectInputsPanelBody key={`${props.site.id}:${generationProjectInputHash(props.site.projectIntent)}`}
    {...props} scopeAllowed={scopeAllowed} />;
}

const SUPPLY_COMPONENT_LABELS: Record<
  'recip' | 'gas-turbine' | 'battery' | 'grid' | 'solar-bess' | 'wind-bess' | 'smr' | 'large-nuclear',
  string
> = {
  recip: 'Reciprocating engines',
  'gas-turbine': 'Gas turbines',
  battery: 'Battery (BESS)',
  grid: 'Utility grid',
  'solar-bess': 'Solar + BESS',
  'wind-bess': 'Wind + BESS',
  smr: 'Small modular reactor (SMR)',
  'large-nuclear': 'Large nuclear',
};

const FIELD_LABEL_CLASS = 'block text-[10px] font-semibold uppercase tracking-[0.12em] text-text-muted';
const FIELD_CONTROL_CLASS = 'mt-1 w-full rounded-md border border-border bg-background p-2 text-xs normal-case tracking-normal text-text-primary disabled:opacity-60';
const FIELD_NUMBER_CLASS = 'mt-1 w-full rounded-md border border-border bg-background p-2 font-mono text-xs normal-case tracking-normal tabular-nums text-text-primary disabled:opacity-60';

function GenerationProjectInputsPanelBody({ site, editingAssessment, readOnly, onSaved, scopeAllowed }: Props & { scopeAllowed: boolean }) {
  const sync = useGenerationProjectInputSync({ site, editingAssessment, enabled: scopeAllowed && !readOnly, onSaved });
  const disabled = readOnly || !scopeAllowed || sync.busy;
  const intent = site.projectIntent;
  const powerHelpId = useId();
  const focusedDemand = useRef<{ phaseId: string | null; value: string } | null>(null);
  const numberEdit = (value: string): number | null => value.trim() === '' ? null : Number(value);
  const save = (changes: GenerationProjectInputChanges) => { void sync.save(changes).catch(() => {}); };
  const loadBasis = intent?.powerBasis ?? '';
  const sortedPhases = intent?.phases?.length
    ? [...intent.phases].sort((a, b) => a.sequence - b.sequence)
    : [null];

  return (
    <section
      aria-label="Shared project inputs"
      className="space-y-4 rounded-md border border-border bg-surface/60 p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-2 border-b border-border/70 pb-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-primary">
            Project Intent Baseline
          </p>
          <h3 className="mt-0.5 text-sm font-semibold text-text-primary">
            Shared project inputs
          </h3>
          <p className="mt-0.5 text-xs text-text-secondary">
            Edits here update Project Intent when you leave a field. Demand, workload and cooling choices are shared; equipment, standby duty and calculated results remain assessment-specific.
          </p>
        </div>
        {sync.message && (
          <p
            role="status"
            className="rounded border border-primary/30 bg-primary/10 px-2.5 py-1 text-[11px] font-medium text-text-primary"
          >
            {sync.message}
          </p>
        )}
      </div>

      {!scopeAllowed && (
        <p
          role="status"
          className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-text-secondary"
        >
          This is an inactive scenario. Its what-if inputs cannot overwrite the active project inputs.
        </p>
      )}

      <p
        id={powerHelpId}
        className="rounded-md border border-border/70 bg-background/60 px-3 py-2 text-xs leading-relaxed text-text-muted"
      >
        Power requirement is the demand to serve. Power basis states whether that MW covers IT equipment or the whole facility.
        {' '}IT MW × PUE = facility MW; facility MW already includes cooling and electrical overhead.
        {' '}Changing the basis keeps the entered quantity unchanged. Leave unknown values blank until decided.
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className={FIELD_LABEL_CLASS}>
          Power basis
          <select
            aria-label="Shared entered load basis"
            aria-describedby={powerHelpId}
            defaultValue={loadBasis}
            disabled={disabled}
            onFocus={sync.captureEdit}
            onChange={event => save({ loadBasis: event.target.value === 'it-load' ? 'it-load' : event.target.value === 'facility' ? 'facility' : null })}
            className={FIELD_CONTROL_CLASS}
          >
            <option value="">Unknown</option>
            <option value="facility">Total facility demand</option>
            <option value="it-load">IT equipment load</option>
          </select>
        </label>

        {sortedPhases.map(phase => (
          <label key={phase?.id ?? 'site'} className={FIELD_LABEL_CLASS}>
            {phase ? `${phase.label} cumulative` : 'Project'} power requirement (MW)
            <input
              aria-label={`Shared ${phase?.label ?? 'project'} demand (MW)`}
              aria-describedby={powerHelpId}
              type="number"
              min="0"
              step="any"
              placeholder="Unknown"
              defaultValue={phase ? phase.capacityMW ?? '' : intent?.powerRequirementMW ?? ''}
              disabled={disabled}
              onFocus={event => {
                sync.captureEdit();
                focusedDemand.current = { phaseId: phase?.id ?? null, value: event.target.value };
              }}
              onBlur={event => {
                const focused = focusedDemand.current;
                focusedDemand.current = null;
                if (!focused || focused.phaseId !== (phase?.id ?? null) || focused.value === event.target.value) return;
                save({ ...(phase ? { phaseId: phase.id } : {}), loadMW: numberEdit(event.target.value) });
              }}
              className={FIELD_NUMBER_CLASS}
            />
          </label>
        ))}

        <label className={FIELD_LABEL_CLASS}>
          PUE (facility ÷ IT)
          <input
            aria-label="Shared PUE"
            aria-describedby={powerHelpId}
            type="number"
            min="1"
            max="10"
            step="any"
            defaultValue={declaredSharedProjectPue(intent) ?? ''}
            placeholder="Unknown"
            disabled={disabled}
            onFocus={sync.captureEdit}
            onBlur={event => save({ pue: numberEdit(event.target.value) })}
            className={FIELD_NUMBER_CLASS}
          />
        </label>
      </div>

      <div className="grid grid-cols-1 gap-3 border-t border-border/60 pt-3 sm:grid-cols-3">
        <label className={FIELD_LABEL_CLASS}>
          Rack cooling
          <select
            aria-label="Shared rack cooling"
            defaultValue={intent?.coolingStrategy ?? 'undecided'}
            disabled={disabled}
            onFocus={sync.captureEdit}
            onChange={event => save({ coolingStrategy: event.target.value === 'air' ? 'air' : event.target.value === 'liquid' ? 'liquid' : event.target.value === 'hybrid' ? 'hybrid' : 'undecided' })}
            className={FIELD_CONTROL_CLASS}
          >
            <option value="undecided">Unknown</option>
            <option value="air">Air</option>
            <option value="liquid">Liquid</option>
            <option value="hybrid">Hybrid</option>
          </select>
        </label>

        <label className={FIELD_LABEL_CLASS}>
          Heat rejection
          <select
            aria-label="Shared heat rejection"
            defaultValue={intent?.bopHeatRejection ?? 'unknown'}
            disabled={disabled}
            onFocus={sync.captureEdit}
            onChange={event => {
              const value = event.target.value;
              if (value === 'dry-cooler' || value === 'wet-cooling-tower' || value === 'hybrid' || value === 'once-through' || value === 'absorption-cchp' || value === 'free-cooling' || value === 'unknown') save({ heatRejection: value });
            }}
            className={FIELD_CONTROL_CLASS}
          >
            <option value="unknown">Unknown</option>
            <option value="dry-cooler">Dry coolers</option>
            <option value="wet-cooling-tower">Wet cooling towers</option>
            <option value="hybrid">Hybrid</option>
            <option value="once-through">Once-through</option>
            <option value="absorption-cchp">Absorption CCHP</option>
            <option value="free-cooling">Free cooling</option>
          </select>
        </label>

        <label className={FIELD_LABEL_CLASS}>
          Workload
          <select
            aria-label="Shared workload"
            defaultValue={intent?.workloadProfile ?? ''}
            disabled={disabled}
            onFocus={sync.captureEdit}
            onChange={event => {
              const value = event.target.value;
              if (value === '') save({ workloadProfile: null });
              else if (value === 'ai-training' || value === 'ai-inference' || value === 'hpc' || value === 'enterprise' || value === 'colocation' || value === 'edge' || value === 'mixed') save({ workloadProfile: value });
            }}
            className={FIELD_CONTROL_CLASS}
          >
            <option value="">Unknown</option>
            <option value="ai-training">AI training</option>
            <option value="ai-inference">AI inference</option>
            <option value="hpc">HPC</option>
            <option value="enterprise">Enterprise</option>
            <option value="colocation">Colocation</option>
            <option value="edge">Edge</option>
            <option value="mixed">Mixed</option>
          </select>
        </label>
      </div>

      {(intent?.phases ?? []).map(phase => (
        <fieldset
          key={phase.id}
          className="space-y-3 rounded-md border border-border/80 bg-background/50 p-3.5"
        >
          <legend className="px-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-text-secondary">
            {phase.label} · shared supply choices
          </legend>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className={FIELD_LABEL_CLASS}>
              Grid relationship
              <select
                aria-label={`Shared ${phase.label} grid relationship`}
                defaultValue={phase.powerSourceConfig?.gridConnection ?? 'undecided'}
                disabled={disabled}
                onFocus={sync.captureEdit}
                onChange={event => save({
                  phaseId: phase.id,
                  gridConnection: event.target.value === 'islanded' ? 'islanded' : event.target.value === 'grid-connected' ? 'grid-connected' : 'undecided',
                })}
                className={FIELD_CONTROL_CLASS}
              >
                <option value="undecided">Unknown</option>
                <option value="grid-connected">Grid-connected</option>
                <option value="islanded">Islanded</option>
              </select>
            </label>

            <label className={FIELD_LABEL_CLASS}>
              Operation target date
              <input
                aria-label={`Shared ${phase.label} operation target date`}
                type="date"
                defaultValue={phase.targetInServiceDate ?? ''}
                disabled={disabled}
                onFocus={sync.captureEdit}
                onBlur={event => save({ phaseId: phase.id, operationTargetDate: event.target.value || null })}
                className={FIELD_CONTROL_CLASS}
              />
            </label>
          </div>

          <div>
            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-text-muted">
              Candidate power sources
            </p>
            <div className="flex flex-wrap gap-2">
              {(['recip', 'gas-turbine', 'battery', 'grid', 'solar-bess', 'wind-bess', 'smr', 'large-nuclear'] as const).map(component => (
                <label
                  key={component}
                  className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs text-text-primary transition-colors hover:border-primary/40 has-[:checked]:border-primary/50 has-[:checked]:bg-primary/10 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60"
                >
                  <input
                    type="checkbox"
                    aria-label={`Shared ${phase.label} ${component}`}
                    disabled={disabled}
                    defaultChecked={phase.powerSourceConfig?.components.includes(component) ?? false}
                    onFocus={sync.captureEdit}
                    onChange={event => {
                      const previous = phase.powerSourceConfig?.components ?? [];
                      save({
                        phaseId: phase.id,
                        powerComponents: event.target.checked
                          ? [...previous, component]
                          : previous.filter(value => value !== component),
                      });
                    }}
                    className="h-3.5 w-3.5 accent-primary"
                  />
                  <span>{SUPPLY_COMPONENT_LABELS[component]}</span>
                </label>
              ))}
            </div>
          </div>

          <p className="text-[11px] text-text-muted">
            These are operator choices. They do not assign equipment capacity, turn alternatives into a combined fleet, or qualify standby as continuous supply.
          </p>
        </fieldset>
      ))}
    </section>
  );
}
